import React, { useEffect, useState, FormEvent, ChangeEvent } from 'react';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { WeaverApiClientInstance } from 'api/weaverApiClientInstance';
import { getRedirectUrl } from 'utils/redirectUrlAfterLogin';
import {
  getDeviceIdCookie,
  saveDeviceIdCookie,
  saveAuthToken,
} from 'utils/tokenStorage';
import {
  loginSuccess,
  getUserProfileSuccess,
  setupAccountSuccess,
  ILoginSuccessPayload,
} from 'actions/authenticate';
import { Location, History } from 'history';
import { MFA_STATE, MFA_ERROR_MSG } from 'utils/constants';
import { Redirect, RouteComponentProps } from 'react-router-dom';
import { routes } from 'utils/routeHelper';
import ButtonMedium from 'components/common/Buttons/ButtonMedium';
import CircularLoader from 'components/common/CircularLoader';
import ErrorInterceptor from 'components/common/ErrorInterceptor';
import InputGeneral from 'components/common/FormControls/FormInputs';
import './styles.scss';
import { AuthClientInstance } from 'api/authClientInstance';

interface IOwnProps {
  location: Location<{ loginState: string; token: string; email: string }>;
  history: History;
}

interface IDispatchProps {
  loginSuccess: (payload: ILoginSuccessPayload) => void;
  getUserProfileSuccess: (profile: UserProfileDTO) => void;
  userLoggedIn: boolean;
  currentUserScopes: string[];
}

type IProps = IOwnProps & RouteComponentProps & IDispatchProps;

const INVALID_QRCODE_STATUS = 401;

const MFARegistration = (props: IProps) => {
  const [errMsg, setErrMsg] = useState('');
  const [codeURI, setCodeURI] = useState('');
  const [passcode, setPassCode] = useState('');

  const generateMFAQRCode = async (params: { token: string }) => {
    try {
      const {
        data: { url: mfaQRCodeURI },
      } = await AuthClientInstance.get().generateMfaToken({
        generateMfaTokenRequestDto: params,
      });

      setCodeURI(mfaQRCodeURI);
    } catch (err) {
      if (err.status === INVALID_QRCODE_STATUS) {
        window.location.replace(routes.LOG_IN);
        return;
      }
      ErrorInterceptor.error(err);
    }
  };

  const onChangeMFAVerifyCode = (e: ChangeEvent<HTMLInputElement>) => {
    setPassCode(e.target.value);
  };

  const onSubmitMFAVerifyCode = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    let deviceId = getDeviceIdCookie();
    if (!deviceId) {
      deviceId = saveDeviceIdCookie();
    }

    try {
      const {
        data: { token },
      } = await AuthClientInstance.get().validateMfaPasscodeAndRegisterDevice({
        verifyMfaTokenRequestDTO: {
          deviceId,
          passcode,
          token: props.location.state.token,
        },
      });
      saveAuthToken(token);
      const { data: profile } = await WeaverApiClientInstance.getUserProfile();
      props.getUserProfileSuccess(profile);
      props.loginSuccess({ token, email: props.location.state.email });
    } catch (e) {
      if (e.status === INVALID_QRCODE_STATUS) {
        window.location.replace(routes.LOG_IN);
        return;
      }
      setErrMsg(MFA_ERROR_MSG);
    }
  };

  useEffect(() => {
    const { state } = props.location;
    if (state && state.loginState === MFA_STATE.MFA_REGISTER && state.token) {
      generateMFAQRCode({ token: state.token });
    } else {
      props.history.push(routes.LOG_IN);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (props.userLoggedIn) {
    return <Redirect to={getRedirectUrl(props.currentUserScopes)} />;
  }

  return (
    <div data-testid="mfa-registration" styleName="wrapper">
      <div styleName="header">
        <div>Protect the privacy of your patients</div>
        <div styleName="sub-header">
          Your organization requires the use of multi-factor authentication.
        </div>
      </div>

      <div styleName="content">
        <div styleName="card">
          <div styleName="card-title">1. Download an app</div>
          <div styleName="card-describe">
            Download a multi-factor authenticator app onto your phone from the
            App Store or Google Play Store.
          </div>
          <div styleName="card-download">
            <div styleName="lock-icon" />
            <div>
              <div>
                <div>Salesforce Authenticator</div>
                <small>(recommended)</small>
              </div>
            </div>
          </div>
        </div>

        <div styleName="card">
          <div styleName="card-title">2. Scan this code</div>
          <div styleName="card-describe">
            Open the authenticator app on your phone and scan this QR code with
            your camera.
          </div>
          <div styleName="card-scan">
            {codeURI && (
              <img src={codeURI} alt="MFA-QR" data-testid="mfa-qr-code" />
            )}

            {!codeURI && (
              <CircularLoader
                wrapperHeight={175}
                wrapperWidth={175}
                top="calc(50% - 20px)"
                left="calc(50% - 18px)"
              />
            )}
          </div>
        </div>

        <div styleName="card">
          <div styleName="card-title">3. Enter the new code</div>
          <div styleName="card-describe">
            The app will generate a verification code. Please enter that below.
          </div>
          <div styleName="card-enter">
            <form
              action="submit"
              styleName="submit-mfa-verify-code-form"
              onSubmit={onSubmitMFAVerifyCode}
            >
              <InputGeneral
                error={!!errMsg}
                label="Verification code"
                name="verifyCode"
                testidProp="mfa-verify-code-input"
                value={passcode}
                onChange={onChangeMFAVerifyCode}
                helperText={errMsg}
                id="filled-error-helper-text"
              />

              <ButtonMedium
                testidProp="mfa-verify-code-submit"
                buttonRole="primary"
                disabled={!passcode}
                buttonType="submit"
                width="100%"
                margin="27px 0 0 0"
              >
                SUBMIT
              </ButtonMedium>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = ({ authenticate }: IRootStateType) => ({
  currentUserScopes: get(authenticate, 'profile.scopes', []),
  userLoggedIn: authenticate.token !== null,
});

export default connect(mapStateToProps, {
  loginSuccess,
  getUserProfileSuccess,
  setupAccountSuccess,
})(MFARegistration);
